<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="cw-header">
        <span class="cw-bar cw-bar--md"></span>
      </div>
      <div class="cw-grid">
        <span
          v-for="(cell, i) in cells"
          :key="i"
          class="cw-cell"
          :class="['--' + cell.state]"
          :style="{ '--cell-i': i }"
        >
          <template v-if="cell.letter">{{ cell.letter }}</template>
        </span>
      </div>
    </template>

    <template #audience>
      <span class="cw-phone-q"></span>
      <div class="cw-phone-clue">
        <span class="cw-phone-clue-label">3 ACROSS</span>
        <span class="cw-bar cw-bar--md"></span>
      </div>
      <div class="cw-phone-input">
        <span class="cw-phone-typed">PLAN<span class="cw-caret">|</span></span>
      </div>
      <div class="cw-phone-keys">
        <span v-for="i in 12" :key="i" class="cw-phone-key"></span>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

type Cell = { state: 'block' | 'empty' | 'fill'; letter?: string }

export default Vue.extend({
  name: 'PreviewCrossword',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  computed: {
    cells(): Cell[] {
      const b: Cell = { state: 'block' }
      const e: Cell = { state: 'empty' }
      const f = (l: string): Cell => ({ state: 'fill', letter: l })
      return [
        b, f('S'), f('T'), f('A'), f('R'),
        f('A'), f('I'), e, e, e,
        b, f('M'), b, b, b,
        f('P'), f('L'), f('A'), f('N'), b,
        b, f('E'), b, b, b,
      ]
    },
  },
})
</script>

<style scoped lang="scss">
.cw-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
}
.cw-bar--md { width: 60%; }

.cw-header {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.cw-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2px;
  flex: 1;
  align-content: center;
  max-width: 180px;
  margin: 0 auto;
}
.cw-cell {
  aspect-ratio: 1 / 1;
  display: grid; place-items: center;
  font-size: 11px; font-weight: 800;
  border-radius: 3px;
}
.cw-cell.--block {
  background: rgba(15, 15, 20, 0.85);
}
.cw-cell.--empty {
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.18);
}
.cw-cell.--fill {
  background: white;
  border: 1px solid var(--accent);
  color: var(--accent);
  opacity: 0;
  animation: cw-fill 4s ease-out infinite;
  animation-delay: calc(var(--cell-i, 0) * 80ms + 400ms);
}

/* ---- Audience phone ---- */
.cw-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.cw-phone-clue {
  display: flex; flex-direction: column; gap: 4px;
  padding: 6px 8px;
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 6px;
}
.cw-phone-clue-label {
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--accent);
}
.cw-phone-input {
  border: 1px solid color-mix(in srgb, var(--accent) 50%, transparent);
  border-radius: 6px;
  padding: 8px 10px;
  background: white;
}
.cw-phone-typed {
  font-size: 14px; font-weight: 800;
  color: rgba(15, 15, 20, 0.85);
  letter-spacing: 0.1em;
}
.cw-caret {
  color: var(--accent);
  animation: cw-blink 1s steps(2) infinite;
}
.cw-phone-keys {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 3px;
  margin-top: auto;
}
.cw-phone-key {
  height: 14px;
  border-radius: 3px;
  background: rgba(15, 15, 20, 0.06);
}

@keyframes cw-fill {
  0%, 12% { opacity: 0; transform: scale(0.6); }
  40%     { opacity: 1; transform: scale(1); }
  92%, 100% { opacity: 1; transform: scale(1); }
}
@keyframes cw-blink {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .cw-cell.--fill { animation: none; opacity: 1; transform: none; }
  .cw-caret { animation: none; }
}
</style>
